import React, { useCallback, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { Alert, Button, ButtonVariant } from "@merge-api/merge-javascript-shared";

import {
  fetchWithoutAuth,
  FormErrorData,
  hasAuthToken,
  setAuthTokenAndUserType,
  SSOLoginData,
  UserSuccessData,
} from "../../../../api-client/APIClient";
import {
  LOGIN_PATH,
  navigateToHomePage,
  navigateToSignUpPage,
  useQuery,
} from "../../../../router/RouterUtils";
import { User } from "../../../../models/Entities";
import { REGEX_EMAIL_ADDRESS } from "../../../shared/utils/SharedComponentUtils";
import { showErrorToast, showInfoToast } from "../../../shared/Toasts";
import { AuthLayout } from "../AuthLayouts";
import AuthPageWrapper from "../shared/AuthPageWrapper";
import useSSOFeatureFlags from "../saml/SamlUtils";

import EmailStep from "./components/EmailStep";
import DetailsStep from "./components/DetailsStep";
import TrustedByPanel from "./components/TrustedByPanel";
import FinishSocialSignUp from "./FinishSocialSignUp";
import { TERMS_REQUIRED_MESSAGE } from "./constants";
import hubspotSignUp from "./HubspotSignUp";
import useSignupTelemetryIds from "./hooks/useSignupTelemetryIds";
import {
  forgetSignupIntent,
  readSignupIntent,
  rememberSignupIntent,
  SignupIntent,
} from "../../../shared/utils/signupIntent";

interface OrgForInviteToken {
  has_saml_provider: boolean;
  saml_is_required: boolean;
  user_with_email_exists: boolean;
  name: string;
}

interface FormData {
  name: string;
  organization?: string;
  email: string;
  password?: string;
  agreed_to_terms_of_service_and_read_privacy_policy: boolean;
}

export interface SignUpUserData extends FormData {
  use_saml?: boolean;
  email_token?: string | null;
  social_auth_provider?: string | null;
  id_token?: string | null;
  signup_intent?: SignupIntent;
}

const parseJWT = (token: string): Record<string, unknown> | undefined => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return undefined;
  }
};

type Props = {
  setUser: (user: User) => void;
};

const SignUpScreen = ({ setUser }: Props) => {
  const history = useHistory();
  const query = useQuery();
  const idToken = query.get("id_token");
  const parsedJWT = idToken ? parseJWT(idToken) : undefined;
  const queryEmail = query.get("email");
  // OIDC `email_verified` is a boolean per the spec but some IdPs emit it as a
  // string. Accept boolean true OR the literal string "true" — reject everything
  // else (including the string "false", which is truthy in JS).
  const jwtEmailVerified =
    parsedJWT?.["email_verified"] === true || parsedJWT?.["email_verified"] === "true";
  const jwtVerifiedEmail =
    parsedJWT && jwtEmailVerified && parsedJWT["email"] === queryEmail ? queryEmail : null;
  const prefilledEmail = parsedJWT ? jwtVerifiedEmail : queryEmail;
  const prefilledName =
    typeof parsedJWT?.["name"] === "string" ? (parsedJWT["name"] as string) : null;
  const inviteToken = query.get("token");
  const createUserToken = query.get("create_user_token");
  const socialAuthProvider = query.get("social_auth_provider");
  const [signupIntent] = useState(() => readSignupIntent(query));

  const hasSocialAuthData = Boolean(socialAuthProvider && idToken && prefilledEmail);
  const hasPrefilledIdentity = Boolean(prefilledEmail);
  const shouldSkipStep1 = Boolean(inviteToken) || hasPrefilledIdentity || hasSocialAuthData;

  const [step, setStep] = useState<"email" | "details">(shouldSkipStep1 ? "details" : "email");
  const [isStep1Exiting, setIsStep1Exiting] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(prefilledEmail ?? "");
  const [name, setName] = useState<string>(prefilledName ?? "");
  const [organization, setOrganization] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [emailStepError, setEmailStepError] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorBanner, setErrorBanner] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isSamlRequired, setIsSamlRequired] = useState<boolean>(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  // True while we're resolving the invite token (gates the Step 2 render so the form
  // doesn't flash with stale state — Password visible / no org subtitle — before the
  // GET /organizations/users/invites/tokens/<token> response settles.
  const [isResolvingInvite, setIsResolvingInvite] = useState<boolean>(Boolean(inviteToken));

  const { isGoogleSSODisabled } = useSSOFeatureFlags();
  const hideGoogleSSO = Boolean(hasSocialAuthData || isSamlRequired || isGoogleSSODisabled);
  const { createAccountButtonGTag, successFormId, preEmailConfirmationFormId } =
    useSignupTelemetryIds({
      hasInviteToken: Boolean(inviteToken),
      isSamlRequired,
      socialAuthProvider,
    });

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleSuccessfulSignUp = useCallback(
    (data: UserSuccessData, hubspotFormId: string) => {
      forgetSignupIntent();
      setAuthTokenAndUserType(data.token, data.user.type);
      setUser(data.user);
      navigateToHomePage(history);
      setIsLoading(false);
      hubspotSignUp(
        {
          name: data.user.name,
          organization: data.user.organization.name,
          email: data.user.email,
        },
        hubspotFormId,
      );
    },
    [history, setUser],
  );

  const handleEmailStepSubmit = () => {
    if (!email.trim() || !REGEX_EMAIL_ADDRESS.test(email)) {
      setEmailStepError("Please enter a valid email address.");
      return;
    }
    setEmailStepError(undefined);
    setIsStep1Exiting(true);
    setTimeout(() => {
      setStep("details");
      setIsStep1Exiting(false);
    }, 200);
  };

  const validateDetailsForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Please enter a name.";
    if (!inviteToken && !organization.trim()) errors.organization = "Please enter a company name.";
    if (!email.trim() || !REGEX_EMAIL_ADDRESS.test(email))
      errors.email = "Please enter a valid email address.";
    if (!isSamlRequired && (!password || password.length < 8 || password.length > 100))
      errors.password = "Please enter a valid password at least 8 characters in length.";
    if (!agreedToTerms)
      errors.agreed_to_terms_of_service_and_read_privacy_policy = TERMS_REQUIRED_MESSAGE;
    return errors;
  };

  const threadBackendErrors = (data: FormErrorData) => {
    const newFieldErrors: Record<string, string> = {};
    for (const field in data) {
      if (field === "non_field_errors") {
        const msg = data[field][0];
        const lower = msg.toLowerCase();
        if (lower.includes("password")) {
          newFieldErrors.password = msg;
        } else if (lower.includes("email")) {
          newFieldErrors.email = msg;
        } else if (lower.includes("company") || lower.includes("organization")) {
          newFieldErrors.organization = msg;
        } else if (lower.startsWith("name") || lower.includes(" name ")) {
          newFieldErrors.name = msg;
        } else {
          setErrorBanner(msg);
        }
        continue;
      }
      if (["name", "organization", "email", "password"].includes(field)) {
        newFieldErrors[field] = data[field][0];
      }
    }
    if (Object.keys(newFieldErrors).length)
      setFieldErrors((prev) => ({ ...prev, ...newFieldErrors }));
  };

  // D6: re-POST /users/create/send-token with the same payload.
  const handleResendEmail = () => {
    if (isLoading) return;
    setIsLoading(true);
    fetchWithoutAuth({
      path: "/users/create/send-token",
      method: "POST",
      body: {
        name,
        organization,
        email,
        password,
        agreed_to_terms_of_service_and_read_privacy_policy: agreedToTerms,
        ...(signupIntent ? { signup_intent: signupIntent } : {}),
      },
      onResponse: () => setIsLoading(false),
      onError: () => {
        showErrorToast("Could not resend the email. Please try again.");
        setIsLoading(false);
      },
    });
  };

  // D6: leave the "Check your email" interstitial, return to Step 2 with values preserved.
  const handleUseDifferentEmail = () => {
    setIsEmailSent(false);
  };

  const performSignUp = () => {
    if (isLoading) return;
    const errors = validateDetailsForm();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsLoading(true);

    // Omit `password` on the SAML-required invite path — the field is hidden, the
    // value is "", and shipping an empty string risks the backend either rejecting
    // it (min-length validation on an unrendered field, invisible failure) or
    // accepting it as a real credential. Backend treats missing `password` together
    // with `use_saml: true` as "no password account".
    const baseUserInfo: SignUpUserData = {
      name,
      organization,
      email,
      agreed_to_terms_of_service_and_read_privacy_policy: agreedToTerms,
      ...(isSamlRequired ? {} : { password }),
    };

    if (inviteToken) {
      const userInfo: SignUpUserData = {
        ...baseUserInfo,
        email_token: inviteToken,
        use_saml: isSamlRequired,
      };
      fetchWithoutAuth({
        path: "/users/create",
        method: "POST",
        body: userInfo,
        onResponse: (data: Partial<UserSuccessData & SSOLoginData>) => {
          if (data.sso_url) {
            window.location.href = (data as SSOLoginData).sso_url;
            return;
          }
          handleSuccessfulSignUp(data as UserSuccessData, successFormId);
        },
        onError: (err) => {
          if (err) {
            err
              .json()
              .then(threadBackendErrors)
              .catch(() => {
                showErrorToast("Something went wrong. Please try again.");
              });
          } else {
            showErrorToast("A network error has occurred. Please try again.");
          }
          setIsLoading(false);
        },
      });
    } else {
      fetchWithoutAuth({
        path: "/users/create/send-token",
        method: "POST",
        body: signupIntent ? { ...baseUserInfo, signup_intent: signupIntent } : baseUserInfo,
        onResponse: () => {
          forgetSignupIntent();
          setIsLoading(false);
          setIsEmailSent(true);
          hubspotSignUp(
            {
              name: baseUserInfo.name,
              organization: baseUserInfo.organization,
              email: baseUserInfo.email,
            },
            preEmailConfirmationFormId,
          );
        },
        onError: (err) => {
          if (err) {
            err
              .json()
              .then(threadBackendErrors)
              .catch(() => {
                showErrorToast("Something went wrong. Please try again.");
              });
          } else {
            showErrorToast("A network error has occurred. Please try again.");
          }
          setIsLoading(false);
        },
      });
    }
  };

  // Finish account creation flow (post-email-verification): user clicked the
  // verification link and arrived with ?create_user_token=…
  const finishAccountCreation = useCallback(
    (token: string) => {
      fetchWithoutAuth({
        path: "/users/create/finish",
        method: "POST",
        body: { token },
        onResponse: (data: UserSuccessData) => handleSuccessfulSignUp(data, successFormId),
        onError: (err) => {
          if (err) {
            err
              .json()
              .then((data: FormErrorData) => {
                const msg = data.non_field_errors?.[0];
                if (msg === "A user already exists with that email address.") {
                  showInfoToast("This email is already registered. Sign in below.");
                  history.push(LOGIN_PATH);
                } else {
                  navigateToSignUpPage(history);
                  setErrorBanner(msg || "Token was invalid, please try again.");
                }
              })
              .catch(() => {
                navigateToSignUpPage(history);
                setErrorBanner("Token was invalid, please try again.");
              });
          } else {
            navigateToSignUpPage(history);
            setErrorBanner("Token was invalid, please try again.");
          }
        },
      });
    },
    [handleSuccessfulSignUp, history, successFormId],
  );

  // The Google round trip and the dashboard both lose ?intent=, so the tab holds on to it.
  useEffect(() => {
    if (signupIntent) rememberSignupIntent(signupIntent);
  }, [signupIntent]);

  useEffect(() => {
    if (createUserToken) {
      finishAccountCreation(createUserToken);
      return;
    }
    if (inviteToken) {
      setIsLoading(true);
      fetchWithoutAuth({
        path: `/organizations/users/invites/tokens/${inviteToken}`,
        method: "GET",
        onResponse: (data: OrgForInviteToken) => {
          if (data.user_with_email_exists) {
            history.replace(LOGIN_PATH);
            return;
          }
          setOrganizationName(data.name);
          setIsSamlRequired(data.saml_is_required);
          setIsLoading(false);
          setIsResolvingInvite(false);
        },
        onError: () => {
          setOrganizationName(null);
          setIsLoading(false);
          setIsResolvingInvite(false);
          setErrorBanner("Token was invalid, please try again.");
        },
      });
    }
  }, [createUserToken, finishAccountCreation, history, inviteToken]);

  if (hasAuthToken()) {
    return <Redirect to="/" />;
  }

  if (createUserToken) {
    return (
      <AuthLayout
        title="Creating your account..."
        description="Hold tight, this should only take a few seconds — if the page is stuck, please refresh and try again."
      />
    );
  }

  if (isEmailSent) {
    return (
      <AuthLayout title="Check your email inbox">
        <div className="flex flex-col gap-3 text-center">
          <p>We've sent you an email to complete account creation.</p>
          <p>
            If you don't see it immediately, please search your inbox for an email from
            hello@merge.dev, check your spam folder, and, for Gmail users, check autofilter
            locations like the Forums folder.
          </p>
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <Button
            type="button"
            fullWidth
            variant={ButtonVariant.SecondaryGray}
            loading={isLoading}
            onClick={handleResendEmail}
          >
            Resend email
          </Button>
          <Button
            type="button"
            fullWidth
            variant={ButtonVariant.TextBlack}
            onClick={handleUseDifferentEmail}
          >
            Use a different email
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Invite branch — show a spinner card until the invite GET resolves, so we don't
  // render the form with stale state (Password field flashing in/out, missing org subtitle).
  if (inviteToken && isResolvingInvite) {
    return <AuthLayout title="Loading your invite..." />;
  }

  if (hasSocialAuthData) {
    return (
      <FinishSocialSignUp
        email={prefilledEmail as string}
        handleSuccessfulSignUp={handleSuccessfulSignUp}
        idToken={idToken as string}
        inviteToken={inviteToken}
        name={prefilledName}
        organizationName={organizationName}
        socialAuthProvider={socialAuthProvider as string}
        signupIntent={signupIntent}
      />
    );
  }

  // Step 2
  if (step === "details") {
    return (
      <AuthPageWrapper backdrop cardMaxWidth="max-w-[497px]">
        <div className="flex flex-col gap-4 w-full">
          {errorBanner && <Alert color="red">{errorBanner}</Alert>}
          <DetailsStep
            email={email}
            setEmail={setEmail}
            emailLocked={Boolean(prefilledEmail)}
            name={name}
            setName={setName}
            nameLocked={Boolean(prefilledName)}
            organization={organization}
            setOrganization={setOrganization}
            showOrganization={!inviteToken}
            organizationName={organizationName}
            password={password}
            setPassword={setPassword}
            showPassword={!isSamlRequired}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
            fieldErrors={fieldErrors}
            clearFieldError={clearFieldError}
            isLoading={isLoading}
            hideGoogleSSO={hideGoogleSSO}
            isSamlRequired={isSamlRequired}
            onSubmit={performSignUp}
            ctaGtagId={createAccountButtonGTag}
            signupIntent={signupIntent}
          />
        </div>
      </AuthPageWrapper>
    );
  }

  // Step 1
  return (
    <AuthPageWrapper belowCard={<TrustedByPanel />} cardMaxWidth="max-w-[472px]">
      <EmailStep
        email={email}
        setEmail={setEmail}
        emailError={emailStepError}
        setEmailError={setEmailStepError}
        onSubmit={handleEmailStepSubmit}
        isLoading={isLoading}
        hideGoogleSSO={hideGoogleSSO}
        isExiting={isStep1Exiting}
        signupIntent={signupIntent}
      />
    </AuthPageWrapper>
  );
};

export default SignUpScreen;
